
import React from "react";
import { i18nManager } from "../../../../i18n";
import { Schemes, Utils } from "../../../../utils";
import { Elements } from "../../../shared/Elements";
import StatefulLink from "../../../shared/StatefulLink";
import { ConfirmDialog, Siren } from "../../../shared/ConfirmDialog";
import { Button, ButtonManageRef, FormControl, NumberInput, Scheme } from "@rivtn/noseur";
import { BaseService, IFactorResponse, IMember, MemberService, SanitizedResponse } from "../../../../services";

export interface ManageDataProps {
    profile: IMember;
}

function ManageData(props: ManageDataProps) {

    const { profile } = props;
    const memberService = MemberService.getInstance();
    const labels = i18nManager.Labels.account.manage_account.manage_data;
    const downloadDataButtonManageRef = React.useRef<ButtonManageRef>(null);

    return (<div className="account-setting">
        <div className="header">
            <span className="title">{i18nManager.Labels.account.manage_account.title} - {labels.title}</span>
            <span className="desc">{labels.desc}</span>
        </div>
        <div className="manage-data">
            <Button raised scheme={Schemes.OMNEHOST} text={labels.request_your_data} style={{ padding: "10px 30px", marginTop: 20, marginBottom: 10 }}
                manageRef={downloadDataButtonManageRef} loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={() => {
                    downloadDataButtonManageRef.current?.setLoadingState(true);
                    memberService.requestDataDownload().then(() => {
                        Siren.alert(labels.data_download_request_sent);
                    }).catch(BaseService.reportError).finally(() => downloadDataButtonManageRef.current?.setLoadingState(false));
                }} />
            {(profile.extra_data?.last_data_download_date) && (<span className="lst-req-on">{labels.your_last_data_request} {Utils.formatToFineDate1(profile.extra_data?.last_data_download_date)}</span>)}
            <span className="link danger" style={{ marginTop: 30 }} onClick={() => TriggerDataCleanup("clear").show()}>{labels.clear_omnehost_data}</span>
            <span className="link danger" style={{ marginTop: 10 }} onClick={() => TriggerDataCleanup("delete").show()}>{labels.delete_omnehost_account}</span>
        </div>
    </div>);

}

export function TriggerDataCleanup(type: "clear" | "delete") {

    const labels = i18nManager.Labels.account.manage_account.manage_data.dialog[type];
    const desc2Parts = labels.desc2.split("${download_your_data}");
    const memberService = MemberService.getInstance();
    const dialogErrorRef = { current: null } as any;
    const confirmationFactor = {
        value: "",
        factor_id: "",
    };

    const contentConstant = (<>
        <span>
            {desc2Parts[0]}
            <StatefulLink label={i18nManager.Labels.account.manage_account.manage_data.dialog.download_your_data} onClick={(done: () => Promise<void>) => {
                memberService.requestDataDownload().then(() => {
                    Siren.alert(i18nManager.Labels.account.manage_account.manage_data.data_download_request_sent);
                }).catch(BaseService.reportError).finally(done);
            }} />
            {desc2Parts[1]}
        </span>
        <a href={labels.article.location} target="_blank" rel="noreferrer" style={{ margin: "20px 0px" }}>{labels.article.label}</a>
    </>);
    const dialog = ConfirmDialog({
        closable: true,
        desc: labels.desc1,
        title: labels.title,
        style: { minWidth: 510 },
        confirmScheme: Scheme.DANGER,
        confirmStyle: { "--noseurSchemeMainColor": "#FF7171" } as any,
        content: (<div className="dialog-content">
            {contentConstant}
            <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
        </div>),
        onConfirm: (button?: ButtonManageRef) => {
            dialogErrorRef.current!.innerText = " ";
            button?.setLoadingState(true);
            if (!confirmationFactor.factor_id) {
                const method = (type === "clear" ? memberService.requestDataClearing : memberService.requestDataDeletion).bind(memberService);
                method().then(({ sanitized }: SanitizedResponse<IFactorResponse>) => {
                    dialog.update({
                        confirmLabel: labels.confirm_label,
                        content: (<div className="dialog-content">
                            {contentConstant}
                            <span>{labels.token_sent}</span>
                            <FormControl isFieldset fill label={i18nManager.Labels.common.token} style={{ marginTop: 20 }}>
                                <NumberInput onInputComplete={(value) => {
                                    confirmationFactor.value = value;
                                    dialogErrorRef.current!.innerText = " "
                                }} />
                            </FormControl>
                            <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
                        </div>),
                    } as any);
                    confirmationFactor.factor_id = sanitized.email_factor_id;
                }).catch(({ errorMessage }: SanitizedResponse<any>) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                }).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            } else {
                if (!confirmationFactor.value) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.account.manage_account.manage_data.dialog.token_required;
                    button?.setLoadingState(false);
                    return false;
                }
                const method = (type === "clear" ? memberService.confirmDataClearing : memberService.confirmDataDeletion).bind(memberService);
                method(confirmationFactor).then(({}: SanitizedResponse<void>) => {
                    dialog.hide();
                    Siren.alert(labels.success);
                }).catch(({ errorMessage }: SanitizedResponse<any>) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                }).finally(() => {
                    button?.setLoadingState(false);
                });
            }
            return false;
        },
    });
    return dialog;

}

export default ManageData;
